import { Link } from "@tanstack/react-router";
import { Lightbulb, Sparkles } from "lucide-react";

/**
 * Small editorial callouts used inside Montessori-koulu articles.
 * They sit inside `.prose-blog`, so headings/text styles are set here
 * explicitly rather than inherited from the prose rhythm.
 */

/** One concrete thing a parent can do today. */
export function KokeileTanaan({ title = "Kokeile tänään", children }: { title?: string; children: React.ReactNode }) {
  return (
    <aside className="not-prose my-8 rounded-2xl border border-sage/30 bg-sage/10 p-6">
      <div className="flex items-start gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sage/15 text-sage">
          <Sparkles className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-sage">{title}</p>
          <div className="mt-2 text-[15px] leading-relaxed text-foreground">{children}</div>
        </div>
      </div>
    </aside>
  );
}

/** Short, carefully worded explanation of the underlying principle. */
export function MiksiToimii({ children }: { children: React.ReactNode }) {
  return (
    <aside className="not-prose my-8 rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-clay/10 text-clay">
          <Lightbulb className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-clay">Miksi tämä toimii?</p>
          <div className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
        </div>
      </div>
    </aside>
  );
}

export type ReadyMadePick = { to: string; label: string; note: string };

/**
 * Optional, clearly separated product section. Educational content stays
 * primary — this only appears where a product genuinely fits the topic.
 */
export function ValmisRatkaisu({
  intro,
  picks,
}: {
  intro: string;
  picks: ReadonlyArray<ReadyMadePick>;
}) {
  return (
    <aside className="not-prose my-10 rounded-2xl border border-border bg-sand/40 p-6 md:p-7">
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Jos etsit valmista ratkaisua</p>
      <p className="mt-3 text-[15px] leading-relaxed text-foreground">{intro}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {picks.slice(0, 4).map((p) => (
          <Link
            key={p.to}
            to={p.to as never}
            className="group flex flex-col gap-1 rounded-xl border border-border bg-card p-4 transition-colors hover:border-foreground"
          >
            <span className="text-sm font-medium text-foreground">{p.label} →</span>
            <span className="text-xs leading-relaxed text-muted-foreground">{p.note}</span>
          </Link>
        ))}
      </div>
    </aside>
  );
}